import { resetCacheData } from "@/cache/GlobalCache";
import { triggerUpdateData } from "@/models/time-control/time-scene";
import type { TimeEventUnion } from "@/models/time-control/timeStateTypes";
import { isAnimationModeOpts } from "@/models/time-control/timeTypeGuard";
import { getAutoRefreshTime, roundTimeToInterval } from "@/models/time-control/timeUtils";
import { DateTime, Duration } from "luxon";
import { assign } from "xstate";
import { buildAnimationModeOpts, getEffectiveRefreshRate } from "./timeDefaults";
import type { AnimationModeOpts, AutoRefreshCtx, TimeContextUnion } from "./timeStateCtxTypes";
import { TimeEventType } from "./timeStateEvents";

const isAfterEvent = (ev: TimeEventUnion) => typeof ev?.type === "string" && ev.type.startsWith("xstate.after");

type SetAnimationEvt = Extract<TimeEventUnion, { type: typeof TimeEventType.setAnimationMode }>;

export const stashAutoRefreshDisplayTime = assign<TimeContextUnion, SetAnimationEvt>((ctx) => {
  // At runtime ctx is AUTO_REFRESH; we only want to store the field in the target context (ANIMATION)
  // → so we just add it on the returned object
  return { ...(ctx as any), lastAutoRefreshTime: (ctx as any).displayTime } as TimeContextUnion;
});
/**
 * Note: If this is raised, you're likely placing the action at the unintended transition path
 * within the state machine. To resolve it, you either want to modify the action to support the context
 * change on the path, or double check if you didn't place the action on the wrong place.
 */
export const wrongActionPlacementError = (actionName: string) => {
  return Error(`${actionName} received an incorrect event. The action must be used in the wrong place.`);
};

// -- Context updater actions start --
// These callbacks are responsible for updating the state context from one state to another.
// TODO: Ideally the input of assigner is a specifc ctx object type, not a union.
//       Then we can remove the runtime event.type check. Currently runtime check is needed
//       because each callback is only usable between certain states.
export const constructAnimationConfig = assign<TimeContextUnion, TimeEventUnion>((ctx, event) => {
  if (event.type !== TimeEventType.updateAnimationMode) {
    throw wrongActionPlacementError(constructAnimationConfig.name);
  }
  const overrides: Partial<AnimationModeOpts> = (event as any).modeOpts ?? {};
  const common = {
    refreshRate: (ctx as any).modeOpts?.refreshRate ?? (overrides as any).refreshRate,
    isLocked: (ctx as any).modeOpts?.isLocked ?? (overrides as any).isLocked,
  };
  const base = (overrides as any).startTime ?? (ctx as any).displayTime;
  const modeOpts = buildAnimationModeOpts(base, common, overrides as any);
  return { ...(ctx as any), modeOpts } as TimeContextUnion;
});

export const updateFullAnimationConfig = assign<TimeContextUnion, TimeEventUnion>((ctx, event) => {
  if (event.type !== TimeEventType.updateFullAnimationConfig) {
    throw wrongActionPlacementError(updateFullAnimationConfig.name);
  }
  return {
    ...ctx,
    displayTime: event.displayTime,
    nextDisplayTime_: event.nextDisplayTime,
    modeOpts: { ...(ctx as any).modeOpts, animationTime_: event.displayTime },
  } as TimeContextUnion;
});

export const constructDefaultAnimationConfig = assign<TimeContextUnion, TimeEventUnion>((ctx, event) => {
  if (event.type !== TimeEventType.setAnimationMode) {
    throw wrongActionPlacementError(constructDefaultAnimationConfig.name);
  }
  const common = {
    refreshRate: (ctx as any).modeOpts?.refreshRate,
    isLocked: !!(ctx as any).modeOpts?.isLocked,
  };
  const modeOpts = buildAnimationModeOpts((ctx as any).displayTime, common);
  return { ...(ctx as any), modeOpts } as TimeContextUnion;
});

export const clearAnimationConfig = assign<TimeContextUnion, TimeEventUnion>((ctx, event) => {
  if (event.type !== TimeEventType.setAutoRefreshMode) {
    throw wrongActionPlacementError(clearAnimationConfig.name);
  }

  const prevModeOpts = ctx.modeOpts as Partial<AnimationModeOpts> | undefined;
  const refreshRate = typeof prevModeOpts?.refreshRate === "number" ? prevModeOpts.refreshRate : 0;
  const isLocked = !!prevModeOpts?.isLocked;

  let previousTime: DateTime;

  if ((ctx as any).lastAutoRefreshTime) {
    previousTime = (ctx as any).lastAutoRefreshTime.setZone(ctx.timezone);
  } else if (isAnimationModeOpts(prevModeOpts) && prevModeOpts.startTime) {
    previousTime = prevModeOpts.startTime.setZone(ctx.timezone);
  } else if (ctx.displayTime) {
    previousTime = ctx.displayTime.setZone(ctx.timezone);
  } else {
    previousTime = DateTime.utc().setZone(ctx.timezone);
  }

  return {
    ...ctx,
    lastAutoRefreshTime: undefined,
    displayTime: previousTime,
    nextDisplayTime_: previousTime,
    modeOpts: { refreshRate, isLocked },
  } as TimeContextUnion;
});

export const autoMoveInTime = assign<TimeContextUnion, TimeEventUnion>((ctx) => {
  const ac = ctx as unknown as AutoRefreshCtx;
  const refreshRate = ac.modeOpts.refreshRate;
  if (refreshRate === 0) {
    return ctx;
  }
  if (ac.modeOpts.isLocked) {
    resetCacheData();
    triggerUpdateData();
    return ctx;
  }
  const newDisplayTime = ac.displayTime.plus(Duration.fromObject({ minutes: refreshRate }));
  return {
    ...(ctx as any),
    nextDisplayTime_: newDisplayTime,
    displayTime: newDisplayTime,
    modeOpts: ac.modeOpts,
  } as TimeContextUnion;
});

export const snapAutoRefreshTimeOnEntry = assign<TimeContextUnion, TimeEventUnion>((ctx, event) => {
  if (isAfterEvent(event)) {
    return ctx;
  }
  const autoRefreshCtx = ctx as unknown as AutoRefreshCtx;
  let displayTime: DateTime;

  if (
    autoRefreshCtx.modeOpts.isLocked ||
    event.type.startsWith("updateRefreshRate") ||
    event.type.startsWith("setAutoRefreshMode")
  ) {
    displayTime = autoRefreshCtx.displayTime.setZone(autoRefreshCtx.timezone);
  } else {
    displayTime = DateTime.now().setZone(autoRefreshCtx.timezone);
  }

  // Snapping grid: for init we want 0 → DEFAULT (snap on default grid), but when the user just set 0 via updateRefreshRate we do NOT snap.
  const gridForInit = getEffectiveRefreshRate(autoRefreshCtx.modeOpts.refreshRate);
  if (!autoRefreshCtx.modeOpts.isLocked && event.type.startsWith("initAutoRefreshMode")) {
    displayTime = getAutoRefreshTime(displayTime, gridForInit);
  }
  if (event.type.startsWith("updateRefreshRate")) {
    const newRate = (event as any).refreshRate;
    if (typeof newRate === "number" && newRate > 0) {
      displayTime = roundTimeToInterval(displayTime, getEffectiveRefreshRate(newRate));
    } // else: user set 0 → do not snap now; will snap on next init
  }

  return {
    ...(ctx as any),
    displayTime,
    nextDisplayTime_: displayTime,
    // Keep original mode options intact (do not persist the effective rounding grid back into refreshRate)
    // so that a configured value of 0 still means "no periodic tick".
    modeOpts: { ...autoRefreshCtx.modeOpts },
  } as TimeContextUnion;
});

export const updateDisplayTime = assign(<T extends TimeContextUnion>(ctx: T, event: TimeEventUnion) => {
  if (event.type !== TimeEventType.updateDisplayTime) {
    throw wrongActionPlacementError(updateDisplayTime.name);
  }
  if (isAnimationModeOpts(ctx.modeOpts)) {
    return {
      ...ctx,
      displayTime: event.displayTime,
      nextDisplayTime_: event.displayTime,
      modeOpts: { ...ctx.modeOpts, animationTime_: event.displayTime },
    };
  }

  return { ...ctx, displayTime: event.displayTime };
});

export const updateNextDisplayTime = assign(<T extends TimeContextUnion>(ctx: T, event: TimeEventUnion) => {
  if (event.type !== TimeEventType.updateNextDisplayTime) {
    throw wrongActionPlacementError(updateDisplayTime.name);
  }
  if (isAnimationModeOpts(ctx.modeOpts)) {
    return {
      ...ctx,
      nextDisplayTime_: event.nextDisplayTime_,
      modeOpts: { ...ctx.modeOpts, animationTime_: event.nextDisplayTime_ },
    };
  }

  return { ...ctx, nextDisplayTime_: event.nextDisplayTime_ };
});

export const updateGuiTimezone = assign((ctx: TimeContextUnion, event: TimeEventUnion): TimeContextUnion => {
  if (event.type !== TimeEventType.updateGuiTimezone) {
    throw wrongActionPlacementError(updateGuiTimezone.name);
  }
  return { ...ctx, timezone: event.timezone };
});

type UpdateRefreshRateEvt = Extract<TimeEventUnion, { type: typeof TimeEventType.updateRefreshRate }>;

export const updateRefreshRateAuto = assign<TimeContextUnion, UpdateRefreshRateEvt>((ctx, ev) => {
  const mo = (ctx as any).modeOpts || {};
  return {
    ...ctx,
    modeOpts: { ...mo, refreshRate: ev.refreshRate, isLocked: mo.isLocked ?? false },
  } as TimeContextUnion;
});

export const updateRefreshRateAnim = assign<TimeContextUnion, UpdateRefreshRateEvt>((ctx, ev) => {
  const mo = (ctx as any).modeOpts;
  if (!isAnimationModeOpts(mo)) {
    // Shape not ready yet → keep it harmless
    return { ...ctx, modeOpts: { refreshRate: ev.refreshRate, isLocked: false } } as TimeContextUnion;
  }
  return { ...ctx, modeOpts: { ...mo, refreshRate: ev.refreshRate } } as TimeContextUnion;
});

export const toggleLockMode = assign<TimeContextUnion, TimeEventUnion>((ctx, event) => {
  if (event.type !== TimeEventType.toggleLockMode) {
    throw wrongActionPlacementError(updateGuiTimezone.name);
  }
  const ac = ctx as unknown as AutoRefreshCtx;
  return { ...ac, modeOpts: { ...ac.modeOpts, isLocked: !ac.modeOpts.isLocked } } as TimeContextUnion;
});

export const ensureAnimationDefaults = assign<TimeContextUnion, TimeEventUnion>((ctx) => {
  const maybe = (ctx as any).modeOpts ?? {};
  if (!isAnimationModeOpts(maybe)) {
    return ctx;
  }
  const mo = maybe as AnimationModeOpts;
  const start = mo.startTime ?? (ctx as any).displayTime;
  const end = mo.endTime ?? start.plus(Duration.fromISO("P2D"));
  const tr = mo.temporalResolution ?? Duration.fromISO("PT1H");
  const anim = mo.animationTime_ ?? start;
  return {
    ...(ctx as any),
    modeOpts: { ...mo, startTime: start, endTime: end, temporalResolution: tr, animationTime_: anim },
  } as TimeContextUnion;
});

/**
 * Shift the whole animation window by +refreshRate minutes.
 * If animationTime_/displayTime are outside of the new [start,end], snap them to new start.
 * Works whether the animation is paused or running (no explicit pause needed).
 */
export const autoShiftAnimationRange = assign<TimeContextUnion, TimeEventUnion>((ctx) => {
  const moAny = (ctx as any).modeOpts as any;
  if (!isAnimationModeOpts(moAny)) {
    return ctx;
  }
  const { startTime, endTime } = moAny;
  if (!(startTime && endTime)) {
    return ctx;
  }

  if (moAny.refreshRate === 0) {
    return ctx;
  }

  if (ctx.modeOpts.isLocked) {
    resetCacheData();
    triggerUpdateData();
    return ctx;
  }

  const rateMin: number = moAny.refreshRate;
  const step = Duration.fromObject({ minutes: Math.max(1, rateMin) });

  const newStart = startTime.plus(step);
  const newEnd = endTime.plus(step);

  const nextFrame =
    moAny.animationTime_ && moAny.animationTime_ >= newStart && moAny.animationTime_ <= newEnd
      ? moAny.animationTime_
      : newStart;
  const nextDisplay =
    (ctx as any).displayTime && (ctx as any).displayTime >= newStart && (ctx as any).displayTime <= newEnd
      ? (ctx as any).displayTime
      : newStart;

  return {
    ...(ctx as any),
    nextDisplayTime_: nextDisplay,
    displayTime: nextDisplay,
    modeOpts: {
      ...moAny,
      startTime: newStart,
      endTime: newEnd,
      animationTime_: nextFrame,
    },
  } as TimeContextUnion;
});

/**
 * Snap the animation window and current frame on (re-)entry.
 *
 * Semantics
 * - This action aligns the animation *window* only on specific events and leaves it unchanged otherwise.
 * - The periodic shift of the window is handled by `after.WINDOW_SHIFT_INTERVAL` + `autoShiftAnimationRange`.
 *
 * Event-specific behavior
 * - `xstate.after.*`    : NO-OP (handled elsewhere by `autoShiftAnimationRange`).
 * - `initAnimationMode` : Snap the window *start* to the refresh-rate grid (minutes); preserve window width.
 * - `updateRefreshRate` : **Move** the window by snapping its *start* to the (new) refresh-rate grid; preserve width.
 * - `setAnimationMode`  : Keep the freshly built defaults from `constructDefaultModeOptions`
 *                         (i.e. `startTime := displayTime`) without snapping.
 * - Other non-`after`   : Keep window unchanged.
 *
 * Notes
 * - The refresh-rate grid (minutes) is derived from `modeOpts.refreshRate` (fallback to default) via `alignMinutes`.
 * - `temporalResolution` still defines the frame spacing inside the window and is not altered here.
 */
export const snapAnimationWindowOnEntry = assign<TimeContextUnion, TimeEventUnion>((ctx, evt) => {
  if (isAfterEvent(evt)) {
    return ctx;
  }

  const modeOpts = ctx.modeOpts; // Union: AutoRefreshModeOpts | ModeOpts
  if (!isAnimationModeOpts(modeOpts)) {
    return ctx;
  }

  const { startTime, endTime, temporalResolution } = modeOpts;
  if (!(startTime && endTime && temporalResolution)) {
    return ctx;
  }

  const width = endTime.diff(startTime);

  // Choose alignment base similar to AutoRefresh:
  // - for locked/updateRefreshRate/setAnimationMode keep existing window (align to startTime)
  // - otherwise (notably on initAnimationMode) use NOW as alignment base
  const isLocked = modeOpts.isLocked;
  const useExistingWindowBase =
    isLocked || evt.type.startsWith("updateRefreshRate") || evt.type.startsWith("setAnimationMode");
  const nowInTz = DateTime.now().setZone((ctx as any).timezone);
  const alignBase = useExistingWindowBase ? startTime : nowInTz;
  // --- Window alignment (event-dependent)
  let snappedStart = startTime;
  let snappedEnd = endTime;

  if (evt.type.startsWith("initAnimationMode")) {
    // For init: if refreshRate = 0, still snap on DEFAULT grid (consistent with AutoRefresh init)
    const gridForInit = getEffectiveRefreshRate(modeOpts.refreshRate);
    snappedStart = roundTimeToInterval(alignBase, gridForInit);
    snappedEnd = snappedStart.plus(width);
  } else if (evt.type.startsWith("updateRefreshRate")) {
    // When the rate changes: snap only if new rate > 0; if new rate = 0, skip snapping now
    const newRate = (evt as any).refreshRate ?? modeOpts.refreshRate;
    if (typeof newRate === "number" && newRate > 0) {
      const grid = getEffectiveRefreshRate(newRate);
      snappedStart = roundTimeToInterval(alignBase, grid); // alignBase === startTime in this branch
      snappedEnd = snappedStart.plus(width);
    } else {
      // newRate === 0 → do not snap now; window stays as-is (will snap on next init)
      snappedStart = startTime;
      snappedEnd = endTime;
    }
  } else if (evt.type.startsWith("setAnimationMode")) {
    // Keep defaults from constructDefaultModeOptions (start := displayTime)
    // Optional: if you decide to align here as well, uncomment the lines below
    // const grid = getEffectiveRefreshRate(modeOpts.refreshRate);
    // snappedStart = roundTimeToInterval(startTime, grid);
  } else {
    // Other non-after events → keep window as-is
  }

  // --- Frame/Display snapping & clamping
  // For consistency we align the frame to the refresh grid as well, but:
  // - on init: 0 → DEFAULT grid
  // - on updateRefreshRate with 0: do not snap now
  const base = modeOpts.animationTime_ ?? ctx.displayTime ?? DateTime.utc();
  const frameGridMin = evt.type.startsWith("initAnimationMode")
    ? getEffectiveRefreshRate(modeOpts.refreshRate)
    : modeOpts.refreshRate > 0
      ? getEffectiveRefreshRate(modeOpts.refreshRate)
      : undefined; // 0 during updateRefreshRate → keep frame as-is

  const snappedFrame = typeof frameGridMin === "number" ? roundTimeToInterval(base, frameGridMin) : base;
  const clampedFrame = (snappedFrame < snappedStart || snappedFrame > snappedEnd ? snappedStart : snappedFrame).setZone(
    (ctx as any).timezone,
  );

  return {
    ...ctx,
    displayTime: clampedFrame,
    nextDisplayTime_: clampedFrame,
    modeOpts: {
      ...modeOpts,
      startTime: snappedStart.setZone((ctx as any).timezone),
      endTime: snappedEnd.setZone((ctx as any).timezone),
      animationTime_: clampedFrame,
    },
  } as TimeContextUnion;
});
